import {useSelector} from 'react-redux';
import {
  selectGiftKeywords,
  selectEvent,
  hasGiftKeywords,
  getGiftKeywordAmazonSearchLink,
} from '~/common/gift-keywords/utils';
import {GuestGiftlist} from '~/common/components/GuestGiftlist/GuestGiftlist';
import {
  BABY_CATEGORY,
  GRADUATION_CATEGORY,
  KIDS_BIRTHDAY_CATEGORY,
  TODDLER_CATEGORY,
  OUTDOOR_GATHERING_EVENT_TYPES,
  WEDDING_EVENT_TYPES,
  ADULT_BIRTHDAY_EVENT_TYPES,
  BIRTHDAY_FOR_TEENS_EVENT_TYPE,
  GRADUATION_EVENT_TYPE,
  BABY_EVENT_TYPES,
  BABYS_FIRST_EVENT_TYPE,
  BIRTHDAY_FOR_KIDS_EVENT_TYPE,
  ADULTS_BIRTHDAY_CATEGORY,
  BIRTHDAY_FOR_TEENS_CATEGORY,
  WEDDING_CATEGORY,
} from '~/common/utils/event_constants';
import {doesMatchEventCategory, doesMatchEventType} from '~/common/utils/event_utils';
import {eviteVendors} from '~/common/vendors/vendors';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {VendorDisc} from '~/ViewInvitation/common/VendorDisc';
import {VendorRect} from '~/ViewInvitation/common/VendorRect';
import {RegistryModule} from '~/ViewInvitation/routes/Landing/RegistryModule';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {mediumAndUp, largishAndUp} from '~libSass/base/_exports.sass';
import {isPreview} from '~/ViewInvitation/utils';
import {DiamondIcon} from '~library/svg/DiamondIcon';

function hasWidget(widgetType, event = window.evite_event) {
  // Copied from classic interface /assets/js/view_invitation/utils.js
  if (!event) return false;
  const widgets = event.widgets || [];
  return widgets.some((w) => w.widget_type === widgetType);
}

function getShowGiftKeywords(event, keywords) {
  return (
    !window.has_gift_registries &&
    !hasWidget('sponsored_registry', event) &&
    hasGiftKeywords(keywords)
  );
}

const preview = isPreview();

export const KeywordList = (props) => {
  const {keywords} = props;
  const filledKeywords = [];
  const pushIfNotEmpty = (lst, str) => {
    if (str.length > 0) lst.push(str);
  };
  keywords.forEach((keyword) => {
    pushIfNotEmpty(filledKeywords, keyword);
  });

  const filledKeywordsWithSetIndex = filledKeywords.map((kw, index) => ({keyword: kw, index}));
  return (
    <div className="view-invitation__gifting-module__gift-ideas__keyword-list">
      {filledKeywordsWithSetIndex.map(({keyword, index}) => (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          key={`${keyword}_${index}`}
          target="_blank"
          data-qa-id={`gift_idea_${index}`}
          href={preview ? undefined : getGiftKeywordAmazonSearchLink(keyword)}
          rel="noreferrer"
          className="button large secondary view-invitation__gifting-module__gift-idea"
        >
          {keyword}
        </a>
      ))}
    </div>
  );
};

export const GuestKeywordsWidget = () => {
  if (!hasGiftKeywords()) return '';

  const keywords = window.evite_event?.gift_keywords || [];
  return (
    <div className="view-invitation__gifting-module__gift-keywords-wrapper">
      <Typography
        variant="header3"
        color="charcoal"
        classNames="view-invitation__gifting-module__gift-keywords-title"
      >
        {`Shop ${window.evite_event?.host_name || ''}'s interests for gift ideas on`}
        <img
          src="https://g0.evitecdn.com/apps/logos/third-party/amazon_logo_plain_large.png"
          className="view-invitation__gifting-module__shop-on-amazon-image"
          alt="Amazon"
        />
      </Typography>
      <KeywordList keywords={keywords} />
    </div>
  );
};

export const getDiscDisplayText = (event) => {
  switch (event?.eventType) {
    case 'halloween':
    case 'halloween_kids':
      return 'Find your costume';

    case 'holiday_party':
    case 'holidayforkids':
    case 'christmas':
    case 'hanukkah':
      return 'Shop holiday gifts';

    default:
      return 'Buy your gift now';
  }
};

export const getTargetURL = (event) => {
  switch (event?.eventType || event?.event_type) {
    case 'halloween':
    case 'halloween_kids':
      return 'https://goto.target.com/jrZRon';

    case 'holiday_party':
    case 'holidayforkids':
    case 'christmas':
    case 'hanukkah':
      return 'https://goto.target.com/AWADEj';

    default:
      return 'https://goto.target.com/mg7n3q';
  }
};

export const getWalmartURL = (event) => {
  const eventClassification = event?.options?.classified_category ?? '';
  const eventType = event?.eventType || event?.event_type;

  if (doesMatchEventType(eventType, OUTDOOR_GATHERING_EVENT_TYPES)) {
    return 'https://www.walmart.com/cp/summer-gathering/8429826?clickid=w6ST7V2hKxycWhd042UQL24mUksT3oyBITWcQQ0&irgwc=1&sourceid=imp_w6ST7V2hKxycWhd042UQL24mUksT3oyBITWcQQ0&veh=aff&wmlspartner=imp_3858077&affiliates_ad_id=565706&campaign_id=9383&sharedid=';
  }

  if (
    doesMatchEventCategory(eventClassification, BIRTHDAY_FOR_TEENS_CATEGORY) ||
    doesMatchEventType(eventType, BIRTHDAY_FOR_TEENS_EVENT_TYPE)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fcp%2Felectronics%2F3944';
  }

  if (
    doesMatchEventCategory(eventClassification, ADULTS_BIRTHDAY_CATEGORY) ||
    doesMatchEventType(eventType, ADULT_BIRTHDAY_EVENT_TYPES)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fcp%2Felectronics%2F3944';
  }

  if (
    doesMatchEventCategory(eventClassification, WEDDING_CATEGORY) ||
    doesMatchEventType(eventType, WEDDING_EVENT_TYPES)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fcp%2Fhome%2F4044%3Fpovid%3DGlobalNav_rWeb_HomeGardenTools_Department_ShopAllHome';
  }

  if (
    doesMatchEventCategory(eventClassification, GRADUATION_CATEGORY) ||
    doesMatchEventType(eventType, GRADUATION_EVENT_TYPE)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fshop%2Fgraduation-gifting%3Fpovid%3DXCATNav_EvergreenGifting_GraduationGifting_GraduationgiftsHighSchool_Shop_Pills_ShopAll';
  }

  if (
    doesMatchEventCategory(eventClassification, BABY_CATEGORY) ||
    doesMatchEventType(eventType, BABY_EVENT_TYPES)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fbrowse%2Fbaby%2Fages-0-12-months%2F5427_491351_1081264';
  }

  if (
    doesMatchEventCategory(eventClassification, TODDLER_CATEGORY) ||
    doesMatchEventType(eventType, BABYS_FIRST_EVENT_TYPE)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fbrowse%2Fbaby%2Fages-12-24-months%2F5427_491351_1081284';
  }

  if (
    doesMatchEventCategory(eventClassification, KIDS_BIRTHDAY_CATEGORY) ||
    doesMatchEventType(eventType, BIRTHDAY_FOR_KIDS_EVENT_TYPE)
  ) {
    return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2Fcp%2Ftoys%2F4171%3Fpovid%3DGlobalNav_rweb_ToysGames_ShopAllToys';
  }

  return 'https://goto.walmart.com/c/3858077/565706/9383?veh=aff&sourceid=imp_000011112222333344&u=https%3A%2F%2Fwww.walmart.com%2F';
};

export const GiftingModule = (props) => {
  const {keywords} = useSelector(selectGiftKeywords);
  const event = useSelector(selectEvent);
  const {matches: mediumScreens} = useMatchQuery(`(min-width: ${mediumAndUp})`);
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const {
    vendorDiscDataQaId = 'af-invite-disc',
    isBottomSheet,
    readonly,
    forceShowCTA,
    connectCTA,
  } = props;
  let {showGiftKeywords = true} = props;

  const isPremium = useSelector((state) => (state.details || state.eventDetails).isPremium);
  const giftingData = useSelector(
    (state) => state.gifting ?? {url: '', iconUrl: '', vendorName: '', title: '', note: ''}
  );
  const registries =
    useSelector((state) =>
      'giftingOptions' in state && state.giftingOptions?.gift_registry_urls.length === 0
        ? null
        : (state.registry?.registries ?? null)
    ) || [];
  showGiftKeywords = showGiftKeywords && getShowGiftKeywords(event, keywords);

  const showGuestGiftlist =
    window.evite_event?.gift_list_url && window.evite_event?.gift_registry_urls.length === 0;

  const displayText = getDiscDisplayText(event);
  const walmartURL = getWalmartURL(event);
  const walmartVendor = eviteVendors.find(({name}) => name === 'Walmart');

  return (
    <div>
      <RegistryModule {...props} />
      {showGuestGiftlist && (
        // TODO: Finish PB-beautifying
        <>
          {registries?.length > 0 && (
            <Divider
              marginBottom={largeScreens ? '2.5rem' : '2rem'}
              marginTop={largeScreens ? '2.5rem' : '2rem'}
            />
          )}
          <div className="giftlist-card gifting__submodule">
            <GuestGiftlist />
          </div>
        </>
      )}
      {showGiftKeywords ? (
        <>
          {(registries?.length > 0 || showGuestGiftlist) && (
            <Divider
              marginTop={largeScreens ? '2.5rem' : '2rem'}
              marginBottom={largeScreens ? '2.5rem' : '2rem'}
            />
          )}
          <GuestKeywordsWidget />
        </>
      ) : null}
      {event.allowGifts && (
        <>
          {(registries?.length > 0 || showGuestGiftlist || showGiftKeywords) && (
            <Divider
              marginTop={0}
              marginBottom={0}
              marginLeft={largeScreens ? '3rem' : '1rem'}
              marginRight={largeScreens ? '3rem' : '1rem'}
              width={null}
            />
          )}
          <div
            className={`submodule-gifting ${
              registries?.length > 0 || showGuestGiftlist || showGiftKeywords
                ? 'with-registries'
                : ''
            }`}
          >
            <div>
              {((preview && !readonly) || forceShowCTA) && (
                <div className="view-invitation__preview-cta multi">
                  <button
                    className="unstyled-button view-invitation__preview-cta"
                    data-qa-id="remove-gifting"
                    id="remove-gifting"
                    ref={(elem) => {
                      if (connectCTA) connectCTA('remove-gifting', elem);
                    }}
                    type="button"
                  >
                    {!isPremium && <DiamondIcon />}
                    Remove
                  </button>
                </div>
              )}
              <div className="view-invitation__gifting-module__vendors">
                <Typography
                  variant={isBottomSheet ? 'paragraph1' : 'header3'}
                  classNames={`view-invitation__registry-module-title${
                    isBottomSheet ? ' in-groundhog-modal' : ''
                  }`}
                  data-qa-id="gifting-module-title"
                >
                  Find the perfect gift
                </Typography>
                <div
                  className={`view-invitation__gifting-module__vendor-list${
                    !mediumScreens ? ' rectangles' : ''
                  }`}
                  data-qa-id="gifting-vendor-list"
                >
                  <Vendor
                    registry={giftingData}
                    displayText={displayText}
                    dataQaId={`${vendorDiscDataQaId}-amazon`}
                    showAsRectangle={!mediumScreens}
                  />
                  <Vendor
                    registry={{
                      ...walmartVendor,
                      vendor_name: walmartVendor.name,
                      url: walmartURL,
                    }}
                    displayText={displayText}
                    dataQaId={`${vendorDiscDataQaId}-walmart`}
                    showAsRectangle={!mediumScreens}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// eslint-disable-next-line react/jsx-props-no-spreading
const Vendor = ({showAsRectangle, ...props}) =>
  showAsRectangle ? <VendorRect {...props} /> : <VendorDisc {...props} />;
